import { useMemo } from 'react'

import { useChannel } from 'hooks/useChannel'
import { useAgentAttendanceShortQuery } from 'modules/api/analytics/queries'
import { UrlStateStorageKeys } from 'modules/url-state-storage/consts'
import { useHashedSearchParams } from 'modules/url-state-storage/useHashedSearchParams'
import { GlobalFilterState } from 'pages/Monitoring/GlobalFilters/GlobalFilters'
import { ResourcesSideParams, SpecialisationHealth } from 'types'
import { getStartOfDay } from 'utils/time/getStartOfDay'
import { getUtcDate } from 'utils/time/getUtcDate'

import { useQueueHealthAgentsParamsMap } from './useQueueHealthAgentsParamsMap'

export const useAgents = (
  queueHealthSpecialisations?: SpecialisationHealth[],
  resourcesSideParams?: ResourcesSideParams,
) => {
  const { channel, regionedChannel } = useChannel()
  const queueHealthAgentsParamsMap = useQueueHealthAgentsParamsMap(
    queueHealthSpecialisations,
    resourcesSideParams?.filterType,
  )

  const { state } = useHashedSearchParams<GlobalFilterState>({
    hashParamKey: UrlStateStorageKeys.GlobalFilters,
    contextKey: regionedChannel,
  })

  const queueId = state.queues ?? []
  const specialisationId = state.specialisations ?? []
  const departmentId = state.departments ?? []
  const areaId = state.areas ?? []

  const startOfDayUtc = getUtcDate(getStartOfDay(new Date(), true)).getTime()

  const agentIds = useMemo(() => {
    const resourcesParamsQueueId = resourcesSideParams?.queue?.id
    const resourcesParamsSpecialisationId = resourcesSideParams?.specialisation?.id

    if (resourcesParamsQueueId) {
      return queueHealthAgentsParamsMap.get(resourcesParamsQueueId)
    }
    if (resourcesParamsSpecialisationId) {
      return queueHealthAgentsParamsMap.get(resourcesParamsSpecialisationId)
    }

    return undefined
  }, [resourcesSideParams, queueHealthAgentsParamsMap])

  const agentsAttendanceShortParams = {
    from: startOfDayUtc,
    to: startOfDayUtc,
    departmentId,
    areaId,
    specialisationId,
    queueId,
    agentId: agentIds ?? [],
    channel: [channel],
  }

  const { data: agents = [], isPending } = useAgentAttendanceShortQuery(
    agentsAttendanceShortParams,
    {
      enabled: Boolean(resourcesSideParams) && Boolean(agentIds?.length),
    },
  )

  return agentIds?.length
    ? {
        agents,
        agentIds,
        isPending,
      }
    : {
        agents: [],
        agentIds: [],
        isPending: false,
      }
}
